import { useAuthStore } from "@/states";
import { AuthStoreType } from "@/states/auth";
import { useQueryClient } from "@tanstack/react-query";
import { App as AntApp, Button } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Clock } from "lucide-react";

/**
 * Formats remaining milliseconds into a human-readable countdown string.
 * Returns "Xh Ym Zs" for durations >= 1 hour, "Xm Zs" for >= 1 minute, or "Xs".
 */
const formatRemaining = (ms: number): string => {
  if (ms <= 0) return "0s";
  const totalSeconds = Math.ceil(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

/**
 * Displays impersonation status banner with a live countdown timer.
 * Handles graceful session exit when the timer reaches zero by refreshing
 * the super admin's session via the refresh token cookie.
 */
export const ImpersonationBanner = () => {
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const token = useAuthStore((state: AuthStoreType) => state.token);
  const user = useAuthStore((state: AuthStoreType) => state.user);
  const authSnapshot = useAuthStore((state: AuthStoreType) => state.authSnapshot);
  const impersonationExpiresAt = useAuthStore((state: AuthStoreType) => state.impersonationExpiresAt);
  const setToken = useAuthStore((state: AuthStoreType) => state.setToken);
  const setUser = useAuthStore((state: AuthStoreType) => state.setUser);
  const clearAuthSnapshot = useAuthStore((state: AuthStoreType) => state.clearAuthSnapshot);

  const [remaining, setRemaining] = useState<number>(() =>
    impersonationExpiresAt ? Math.max(0, impersonationExpiresAt - Date.now()) : 0,
  );
  const [isExiting, setIsExiting] = useState(false);
  const isExitingRef = useRef(false);

  /**
   * Restores the super admin session by refreshing via the cookie,
   * restoring the saved admin profile, and redirecting to the selected organisation users list.
   */
  const exitImpersonation = useCallback(
    async (reason: "manual" | "expired") => {
      if (isExitingRef.current || !authSnapshot) return;
      isExitingRef.current = true;
      setIsExiting(true);

      try {
        // Refresh using super admin's cookie to get a fresh access token
        const { data } = await axios.post(
          "/api/auth/refresh",
          {},
          { withCredentials: true, _skipAuthRefresh: true } as any,
        );

        const newAdminToken = data.token;

        // Cancel any in-flight requests that might overwrite the restored session
        await queryClient.cancelQueries({ queryKey: ["me"] });

        // Restore super admin session in the store
        setUser(authSnapshot.user);
        setToken(newAdminToken);
        clearAuthSnapshot();

        if (reason === "expired") {
          message.info("Impersonation session ended. Returning to admin.");
        }
      } catch (error) {
        console.error("Failed to restore admin session on impersonation exit", error);
        // Fallback: use the stale snapshot token if refresh fails
        if (authSnapshot) {
          setUser(authSnapshot.user);
          setToken(authSnapshot.token);
          clearAuthSnapshot();
          message.error("Session refresh failed. Restored cached admin session.");
        }
      }finally{
        // Hard redirect to exit the impersonated context entirely
        window.location.replace(authSnapshot?.returnTo || "/admin/tenants");
      }
    },
    [authSnapshot, setToken, setUser, clearAuthSnapshot, queryClient, message],
  );

  // Live countdown timer — ticks every second
  useEffect(() => {
    if (!impersonationExpiresAt || isExiting) return;

    let intervalId: ReturnType<typeof setInterval>;

    const tick = () => {
      const left = Math.max(0, impersonationExpiresAt - Date.now());
      setRemaining(left);

      if (left <= 0) {
        if (intervalId) clearInterval(intervalId);
        exitImpersonation("expired");
      }
    };

    tick(); // Immediately sync on mount/change
    intervalId = setInterval(tick, 1000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [impersonationExpiresAt, isExiting, exitImpersonation]);

  if (!token || !authSnapshot) return null;

  // Determine urgency level for timer color
  const isUrgent = remaining > 0 && remaining <= 5 * 60 * 1000; // Last 5 minutes
  const isCritical = remaining > 0 && remaining <= 60 * 1000; // Last 60 seconds

  return (
    <div className="fixed top-16 left-0 right-0 bg-amber-100 border-b border-amber-200 text-amber-900 px-4 py-2 mb-2 z-40">
      <div className="container mx-auto flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-3">
          <span className="font-semibold">Impersonating as {user?.fullname || "User"}</span>
          {user?.tenant?.name ? (
            <span>({user.tenant.name})</span>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {impersonationExpiresAt && remaining > 0 && (
            <span
              className={`flex items-center gap-1 font-mono text-xs font-medium ${
                isCritical
                  ? "text-red-700"
                  : isUrgent
                    ? "text-orange-700"
                    : "text-amber-700"
              }`}
            >
              Session Expires In: 
              <Clock size={14} />
              {formatRemaining(remaining)}
            </span>
          )}
          <Button
            size="small"
            loading={isExiting}
            onClick={() => exitImpersonation("manual")}
          >
            Exit Now
          </Button>
        </div>
      </div>
    </div>
  );
};
